import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Globe, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import Logo from "./Logo";
import { t } from "../i18n";
import { SLUGS, resolvePage, resolveLegacy, staticPath, pagePath, staticSlugToKey } from "../data";

function alternatePath(pathname, to) {
  const segs = pathname.split("/").filter(Boolean);
  const from = segs[0] === "it" ? "it" : "en";
  if (segs.length <= 1) return `/${to}`;
  const second = segs[1];
  if (second === SLUGS.carRental[from]) {
    if (segs.length === 2) return staticPath(to, "carRental");
    const page =
      segs.length === 3
        ? resolvePage(from, segs[2])
        : resolveLegacy(from, segs[2], segs[3], segs[4]);
    return page ? pagePath(to, page) : `/${to}`;
  }
  const key = staticSlugToKey(from, second);
  return key ? staticPath(to, key) : `/${to}`;
}

export default function Navbar({ lang }) {
  const i = t[lang];
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: i.nav.carRental, to: staticPath(lang, "carRental"), id: "nav-car-rental" },
    { label: i.nav.airports, to: staticPath(lang, "airports"), id: "nav-airports" },
    { label: i.nav.deals, to: staticPath(lang, "deals"), id: "nav-deals" },
    { label: i.nav.help, to: staticPath(lang, "help"), id: "nav-help" },
  ];
  const otherLang = lang === "en" ? "it" : "en";

  return (
    <header
      className={`glass-header sticky top-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_30px_rgba(0,95,115,0.08)]" : ""
      }`}
    >
      <div className="container-site flex h-16 items-center justify-between gap-4 md:h-20">
        <Logo lang={lang} />

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Main">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={l.id}
              className="text-sm font-medium text-foreground/80 transition-colors duration-200 hover:text-petrol"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <span
            className="hidden rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground/70 sm:inline-block"
            title={lang === "it" ? "La valuta si sceglie nel widget di ricerca" : "Currency is selected inside the search widget"}
            data-testid="currency-badge"
          >
            EUR
          </span>
          <Link
            to={alternatePath(location.pathname, otherLang)}
            data-testid="lang-switch"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground/80 transition-colors duration-200 hover:border-petrol hover:text-petrol"
            aria-label={lang === "en" ? "Passa all'italiano" : "Switch to English"}
          >
            <Globe className="h-3.5 w-3.5" />
            {otherLang.toUpperCase()}
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-open"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-foreground transition-colors duration-200 hover:border-petrol lg:hidden"
                aria-label={i.nav.menu}
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white p-0">
              <div className="flex items-center justify-between border-b border-border px-6 py-5">
                <Logo lang={lang} />
                <SheetClose asChild>
                  <button data-testid="mobile-menu-close" aria-label="Close menu" className="rounded-full p-2 transition-colors duration-200 hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </SheetClose>
              </div>
              <nav className="flex flex-col px-6 py-6" aria-label="Mobile">
                {[...links, { label: i.nav.about, to: staticPath(lang, "about"), id: "nav-about" }, { label: i.nav.contact, to: staticPath(lang, "contact"), id: "nav-contact" }].map((l) => (
                  <SheetClose asChild key={l.id}>
                    <Link
                      to={l.to}
                      data-testid={`mobile-${l.id}`}
                      className="border-b border-border/60 py-4 font-display text-lg font-bold tracking-tight text-foreground transition-colors duration-200 hover:text-petrol"
                    >
                      {l.label}
                    </Link>
                  </SheetClose>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
